// v77.99.18 — Idempotency-Key helper for client mutations.
//
// HOW TO USE THIS HELPER:
//
//   import { useIdempotencyKey, idempotencyHeader } from "@/lib/idempotency";
//
//   function MyForm() {
//     // One key per form instance — rotated on success.
//     const { key, rotate } = useIdempotencyKey();
//
//     const onSubmit = async () => {
//       try {
//         await api.post("/some/endpoint", payload, {
//           headers: idempotencyHeader(key),
//         });
//         rotate(); // fresh key for the next submission
//       } catch (e) {
//         // Leave the key as-is so a retry hits the SAME server-side
//         // idempotency slot and either short-circuits or runs once.
//       }
//     };
//   }
//
// Why a hook + helper instead of axios interceptor:
//   * Generic interceptor would attach the same key to every retry of
//     the same request, which is what we want, BUT it'd also attach it
//     to unrelated calls (GET filters, polling, /me) and fill the
//     server-side idempotency_keys collection with noise.
//   * Per-form key gives us precise control: critical mutations
//     (create, send, invite, settle, propose, export) get a key,
//     everything else is unchanged.

import { useCallback, useState } from "react";

/**
 * Generate a fresh idempotency key. Uses `crypto.randomUUID` where
 * available (every browser since 2022) with a JS fallback so the
 * helper still works in any embedded webview without crypto.
 *
 * Keys are opaque to the server — only their identity matters.
 */
export function generateIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * React hook holding a single idempotency key with rotation.
 * Returns `{ key, rotate }`. Call `rotate()` after a successful
 * mutation so the next submission gets a fresh slot.
 */
export function useIdempotencyKey() {
  const [key, setKey] = useState(() => generateIdempotencyKey());
  const rotate = useCallback(() => setKey(generateIdempotencyKey()), []);
  return { key, rotate };
}

/**
 * Pack the key into the standard `Idempotency-Key` header. Returns an
 * empty object when no key is supplied so callers can spread the
 * result conditionally:
 *
 *   await api.post(url, body, { headers: idempotencyHeader(key) });
 */
export function idempotencyHeader(key) {
  if (!key) return {};
  return { "Idempotency-Key": String(key) };
}

export default useIdempotencyKey;
